import { useState } from "react";
import {
  Bot,
  Zap,
  Users,
  BarChart,
  Calendar,
  MessageSquare,
  Rocket,
  LogOut,
  ShieldCheck,
  Shield,
  Gift,
} from "lucide-react";
import { CreditsDisplay } from "./CreditsDisplay";
import { useAuth } from "../lib/AuthProvider";
import { MFASettings } from "./MFASettings";

interface HeaderProps {
  onNavigate?: (view: "scheduler" | "social" | "ads" | "gapli") => void;
  currentView?: "scheduler" | "social" | "ads" | "gapli";
}

export function Header({ onNavigate, currentView = "scheduler" }: HeaderProps) {
  const { signOut, user, mfaVerified, refreshMfaStatus } = useAuth();
  const [showMfaSettings, setShowMfaSettings] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <>
    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-4 sm:py-6 px-4 sm:px-6 lg:px-8 shadow-md">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center mb-4">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
            Zaplanuj swój automatyczny biznes
          </h1>
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <CreditsDisplay />
            {onNavigate && (
              <div className="flex space-x-2">
                <button
                  onClick={() => onNavigate("scheduler")}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
                    currentView === "scheduler"
                      ? "bg-white text-blue-600"
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                >
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Zaplanowane</span>
                </button>
                <button
                  onClick={() => onNavigate("social")}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
                    currentView === "social"
                      ? "bg-white text-blue-600"
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                >
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Social Inbox</span>
                </button>
              </div>
            )}
            {user && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onNavigate && onNavigate("ads")}
                  className={`flex items-center space-x-1 px-2 sm:px-3 py-2 rounded-lg transition-colors text-sm ${
                    currentView === "ads"
                      ? "bg-white text-blue-600"
                      : "bg-white/10 hover:bg-white/20 text-white/70"
                  }`}
                  title="Marketing Campaigns"
                >
                  <Rocket className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline ml-1">Marketing</span>
                </button>
                <button
                  onClick={() => onNavigate && onNavigate("gapli")}
                  className={`flex items-center space-x-1 px-2 sm:px-3 py-2 rounded-lg transition-all text-sm ${
                    currentView === "gapli"
                      ? "bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/30"
                      : "bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 border border-purple-500/30"
                  }`}
                  title="Promuj Gapli — Kampania Partnerska"
                >
                  <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline ml-1">Promuj Gapli</span>
                </button>
                <button
                  onClick={() => setShowMfaSettings(true)}
                  className={`flex items-center space-x-1 px-2 sm:px-3 py-2 rounded-lg transition-colors text-sm ${
                    mfaVerified
                      ? 'bg-green-500/80 hover:bg-green-600 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white/70'
                  }`}
                  title={mfaVerified ? '2FA Active — Click to manage' : 'Set up 2FA'}
                >
                  {mfaVerified ? (
                    <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                  <span className="hidden sm:inline ml-1">{mfaVerified ? '2FA' : '2FA'}</span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center w-9 h-9 rounded-lg transition-colors bg-red-500/80 hover:bg-red-600 text-white"
                  title="Wyloguj się"
                >
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
        <p className="text-blue-100 text-sm sm:text-base lg:text-lg mb-3 sm:mb-4">
          Obserwuj jak Sztuczna Inteligencja pozyskuje dla Ciebie nowych
          klientów
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
            <div className="bg-blue-500 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm sm:text-base">
                Automatyzacja treści
              </h3>
              <p className="text-xs sm:text-sm text-blue-100">
                AI tworzy angażujące posty dostosowane do Twojej branży
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
            <div className="bg-blue-500 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm sm:text-base">
                Regularne publikacje
              </h3>
              <p className="text-xs sm:text-sm text-blue-100">
                Zaplanuj posty z wyprzedzeniem i utrzymuj stałą obecność
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
            <div className="bg-blue-500 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm sm:text-base">
                Pozyskiwanie klientów
              </h3>
              <p className="text-xs sm:text-sm text-blue-100">
                Zwiększ zasięg i przyciągnij nowych odbiorców
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
            <div className="bg-blue-500 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <BarChart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm sm:text-base">
                Analiza wyników
              </h3>
              <p className="text-xs sm:text-sm text-blue-100">
                Śledź efekty i optymalizuj swoją strategię
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* MFA Settings Modal */}
    {showMfaSettings && user && (
      <MFASettings
        userId={user.id}
        onClose={() => setShowMfaSettings(false)}
        onStatusChange={() => {
          refreshMfaStatus();
          setShowMfaSettings(false);
        }}
      />
    )}
    </>
  );
}
